import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { QrCode, User, Lock, ArrowRight, AlertCircle } from 'lucide-react';
import { useAuth } from '../AuthContext'; // Adjust path as needed

// Utility function for class names
function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

// QAOpsLoginScreen Component
export default function QAOpsLoginScreen({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordShow, setPasswordShow] = useState(false);

  const { login, isLoading } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    // Basic validation
    if (!username.trim() || !password.trim()) {
      setError("Please enter both username and password");
      setIsSubmitting(false);
      return;
    }

    try {
      const result = await login(username, password, rememberMe);
      
      if (result.success) {
        // Call the parent onLogin callback if provided
        if (onLogin) {
          onLogin(username, password);
        }
      } else {
        setError(result.error || "Login failed");
      }
    } catch (error) {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <div className="login-container">
      <style jsx>{`
        .login-container {
          min-height: 100vh;
          background: #e9ecef;
          position: relative;
          overflow: hidden;
          display: flex;
          align-items: center;
        }

        .svg-pattern-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          opacity: 1;
          pointer-events: none;
        }

        .wave-decoration {
          position: absolute;
          top: 0;
          left: -150px;
          width: 40%;
          height: 100%;
          background: linear-gradient(90deg, rgba(0, 123, 255, 0.06) 0%, transparent 100%);
          clip-path: ellipse(100% 55% at 0% 50%);
          pointer-events: none;
        }

        .curved-lines-left {
          position: absolute;
          top: 30%;
          left: 15%;
          width: 200px;
          height: 200px;
          pointer-events: none;
        }

        .curved-lines-left::before,
        .curved-lines-left::after {
          content: '';
          position: absolute;
          width: 150px;
          height: 150px;
          border: 2px solid rgba(0, 123, 255, 0.12);
          border-radius: 50%;
          border-right-color: transparent;
          border-bottom-color: transparent;
          transform: rotate(-45deg);
        }

        .curved-lines-left::after {
          width: 100px;
          height: 100px;
          top: 25px;
          left: 25px;
          border-color: rgba(0, 123, 255, 0.08);
          border-right-color: transparent;
          border-bottom-color: transparent;
        }

        .wave-bottom-right {
          position: absolute;
          bottom: -100px;
          right: -100px;
          width: 300px;
          height: 300px;
          background: radial-gradient(circle, rgba(0, 123, 255, 0.08) 0%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
        }

        .left-section {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding-left: 8%;
          padding-right: 3%;
          position: relative;
          z-index: 5;
          text-align: right;
        }

        .right-section {
          flex: 0 0 450px;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          padding: 2rem;
          padding-left: 0;
          position: relative;
          z-index: 10;
        }

        .qas-title {
          font-size: 5rem;
          font-weight: 200;
          letter-spacing: 10px;
          color: rgba(0, 123, 255, 0.5);
          margin-bottom: 0.5rem;
          line-height: 1;
        }

        .qas-subtitle {
          font-size: 1.5rem;
          font-weight: 300;
          color: #495057;
          letter-spacing: 2px;
          margin-bottom: 0.5rem;
        }

        .qas-version {
          font-size: 1.2rem;
          font-weight: 400;
          color: #6c757d;
          letter-spacing: 1px;
          margin-bottom: 0.25rem;
        }

        .qas-mode {
          font-size: 1rem;
          font-weight: 500;
          color: #007bff;
          letter-spacing: 1px;
          text-transform: uppercase;
          padding: 0.25rem 0.75rem;
          background: rgba(0, 123, 255, 0.08);
          border-radius: 4px;
          display: inline-block;
          margin-top: 0.5rem;
        }

        .login-wrapper {
          width: 100%;
          max-width: 400px;
        }

        .daikin-logo {
          position: relative;
          text-align: center;
          margin-bottom: 2rem;
          z-index: 20;
        }

        .daikin-logo img {
          height: 40px;
          display: inline-block;
        }

        .login-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          width: 100%;
          padding: 2.5rem;
          position: relative;
        }

        .login-card:hover {
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15);
        }

        .form-input {
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          padding: 0.75rem 1rem;
          padding-left: 2.5rem;
          border-radius: 8px;
          width: 100%;
          transition: all 0.3s ease;
          font-size: 0.95rem;
        }

        .form-input:focus {
          outline: none;
          background: white;
          border-color: #007bff;
          box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
        }

        .form-input:hover {
          background: white;
        }

        .btn-primary-custom {
          background: #007bff;
          color: white;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          border: none;
          width: 100%;
          font-weight: 500;
          font-size: 1rem;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .btn-primary-custom:hover:not(:disabled) {
          background: #0056b3;
          transform: translateY(-1px);
          box-shadow: 0 5px 15px rgba(0, 123, 255, 0.3);
        }

        .btn-primary-custom:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .input-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #495057;
          pointer-events: none;
          z-index: 1;
        }

        .password-toggle {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #495057;
          cursor: pointer;
          padding: 5px;
          z-index: 1;
        }

        .password-toggle:hover {
          color: #212529;
        }

        @media (max-width: 1024px) {
          .left-section {
            display: none;
          }
          
          .right-section {
            flex: 1;
            max-width: 100%;
            padding-left: 2rem;
            justify-content: center;
          }

          .daikin-logo {
            margin-bottom: 1.5rem;
          }

          .curved-lines-left {
            display: none;
          }

          .qas-mobile {
            display: block;
            text-align: center;
            margin-bottom: 2rem;
          }

          .qas-mobile h1 {
            font-size: 3rem;
            font-weight: 200;
            letter-spacing: 6px;
            color: rgba(0, 123, 255, 0.45);
          }

          .qas-mobile .mode-badge {
            display: inline-block;
            font-size: 0.75rem;
            font-weight: 600;
            color: #007bff;
            background: rgba(0, 123, 255, 0.1);
            padding: 0.25rem 0.5rem;
            border-radius: 4px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-top: 0.5rem;
          }
        }

        @media (min-width: 1025px) {
          .qas-mobile {
            display: none;
          }
        }
      `}</style>

      {/* SVG Pattern Background */}
      <svg className="svg-pattern-overlay" xmlns="http://www.w3.org/2000/svg">
        <defs>
          {/* Dots Pattern */}
          <pattern id="dots" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1.5" fill="rgba(0, 123, 255, 0.18)"/>
            <circle cx="12" cy="12" r="1.5" fill="rgba(0, 123, 255, 0.18)"/>
          </pattern>

          {/* Wave Lines Pattern */}
          <pattern id="waves" x="0" y="0" width="100" height="20" patternUnits="userSpaceOnUse">
            <path d="M0 10 Q 25 5 50 10 T 100 10" stroke="rgba(0, 123, 255, 0.12)" strokeWidth="1.5" fill="none"/>
          </pattern>

          {/* Grid Pattern */}
          <pattern id="grid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(0, 123, 255, 0.08)" strokeWidth="1"/>
          </pattern>

          {/* Circles Pattern */}
          <pattern id="circles" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
            <circle cx="30" cy="30" r="20" fill="none" stroke="rgba(0, 123, 255, 0.06)" strokeWidth="1"/>
            <circle cx="30" cy="30" r="10" fill="none" stroke="rgba(0, 123, 255, 0.08)" strokeWidth="1"/>
          </pattern>
        </defs>
        
        {/* Apply patterns to different areas */}
        <rect width="25%" height="100%" fill="url(#circles)"/>
        <rect x="25%" width="25%" height="100%" fill="url(#dots)"/>
        <rect x="50%" width="25%" height="100%" fill="url(#waves)"/>
        <rect x="75%" width="25%" height="100%" fill="url(#grid)"/>
      </svg>

      {/* Additional Background Decorative Elements */}
      <div className="wave-decoration"></div>
      <div className="curved-lines-left"></div>
      <div className="wave-bottom-right"></div>

      {/* Left Section - QAS Branding */}
      <div className="left-section">
        <div>
          <h1 className="qas-title">QAS</h1>
          <p className="qas-subtitle">Quality Audit System</p>
          <p className="qas-version">Version 1.0</p>
          <span className="qas-mode">AUDITOR Mode</span>
        </div>
      </div>

      {/* Right Section - Login Form */}
      <div className="right-section">
        <div className="login-wrapper">
          {/* Daikin Logo - Centered above login card */}
          <div className="daikin-logo">
            <img 
              src="/assets/images/desktop-logo.png"
              alt="Daikin Applied" 
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<span style="color: #6c757d; font-size: 1rem; font-weight: 500;">DAIKIN APPLIED</span>';
              }}
            />
          </div>

          {/* Login Card */}
          <div className="login-card">
            {/* Mobile QAS Logo */}
            <div className="qas-mobile">
              <h1>QAS</h1>
              <span className="mode-badge">Admin Mode</span>
            </div>

            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome Back</h2>
              <p className="text-gray-600 text-sm font-medium">Sign in to continue to your dashboard</p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm rounded">
                <div className="flex items-center">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  {error}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Username Field */}
                <div>
                  <label htmlFor="username" className="block text-sm font-semibold text-gray-800 mb-2">
                    Username
                  </label>
                  <div className="relative">
                    <User className="input-icon w-5 h-5" />
                    <input
                      type="text"
                      name="username"
                      id="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={isSubmitting || isLoading}
                      className="form-input"
                      placeholder="Enter your username"
                      autoComplete="username"
                      required
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label htmlFor="password" className="block text-sm font-semibold text-gray-800">
                      Password
                    </label>
                    <a href="#" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                      Forgot password?
                    </a>
                  </div>
                  <div className="relative">
                    <Lock className="input-icon w-5 h-5" />
                    <input
                      name="password"
                      type={passwordShow ? 'text' : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="form-input pr-10"
                      id="password"
                      placeholder="Enter your password"
                      disabled={isSubmitting || isLoading}
                      autoComplete="current-password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setPasswordShow(!passwordShow)}
                      className="password-toggle"
                      disabled={isSubmitting || isLoading}
                    >
                      {passwordShow ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Remember Me */}
                <div className="flex items-center">
                  <input
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    type="checkbox"
                    name="remember_me"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    id="remember_me"
                    disabled={isSubmitting || isLoading}
                  />
                  <label htmlFor="remember_me" className="ml-2 block text-sm text-gray-700 font-medium">
                    Remember me for 2 weeks
                  </label>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting || isLoading || !username || !password}
                  className="btn-primary-custom mt-4"
                >
                  {isSubmitting || isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Signing In...
                    </span>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </div>
            </form>

            {/* Sign Up Link */}
            <div className="mt-6 text-center pt-6 border-t border-gray-100">
              <p className="text-sm text-gray-700 font-medium">
                Don't have an account?{' '}
                <a href="#" className="font-semibold text-blue-600 hover:text-blue-700">
                  Sign Up
                </a>
              </p>
            </div>

            {/* Daikin Applied Europe Text */}
            <div className="text-center mt-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                Daikin Applied Europe
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}